import { useKeycloak } from "@react-keycloak/web";
import { Button, Empty, Popconfirm, Select, Table } from "antd";
import { useState } from "react";
import { useAppSelector } from "../../../app/store";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  useDeleteGlobalHolidayMutation,
  useDeleteSingleGlobalHolidayMutation,
  useGetGlobalHolidaysQuery,
} from "../../../features/holiday/holidayApi";
import HolidayImportModal from "./HolidayImportModal";
import { toast } from "../../../lib/toast";

export default function HolidayCalender() {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const { role } = useAppSelector((state) => state.auth);
  const { keycloak } = useKeycloak();
  const importApiKey =
    process.env.REACT_APP_IMPORT_GLOBAL_API_KEY ||
    localStorage.getItem("import_global_api_key");
  const { data, isLoading, isFetching } = useGetGlobalHolidaysQuery({
    params: {
      skip: 0,
      limit: 500,
      year: selectedYear,
      search: "",
    },
  }, {
    skip: !importApiKey,
  });
  const [deleteGlobalHoliday, { isLoading: isDeletingGlobalHoliday }] =
    useDeleteGlobalHolidayMutation();
  const [deleteSingleGlobalHoliday, { isLoading: isDeletingSingleHoliday }] =
    useDeleteSingleGlobalHolidayMutation();

  const isSuperAdmin =
    role === "SUPER_ADMIN" ||
    keycloak?.realmAccess?.roles?.includes("super_admin");
  const holidays = data?.data?.holidays || [];
  const handleDeleteGlobalHolidays = async () => {
    try {
      const response: any = await deleteGlobalHoliday({
        year: selectedYear,
      }).unwrap();
      toast(
        "success",
        response?.message || "Global holidays deleted successfully"
      );
    } catch (error: any) {
      const serverMessage = error?.data?.message;
      const normalizedServerMessage =
        Array.isArray(serverMessage) && serverMessage.length
          ? serverMessage.join(", ")
          : typeof serverMessage === "string"
          ? serverMessage
          : undefined;
      const message =
        normalizedServerMessage ||
        error?.error ||
        error?.message ||
        "Failed to delete global holidays";
      toast("error", message);
    }
  };

  const handleDeleteSingleGlobalHoliday = async (holiday: any) => {
    try {
      const response: any = await deleteSingleGlobalHoliday({
        title: holiday?.title,
        start_date: holiday?.start_date,
        end_date: holiday?.end_date,
      }).unwrap();
      toast(
        "success",
        response?.message || "Global holiday deleted successfully"
      );
    } catch (error: any) {
      const serverMessage = error?.data?.message;
      const normalizedServerMessage =
        Array.isArray(serverMessage) && serverMessage.length
          ? serverMessage.join(", ")
          : typeof serverMessage === "string"
          ? serverMessage
          : undefined;
      const message =
        normalizedServerMessage ||
        error?.error ||
        error?.message ||
        "Failed to delete global holiday";
      toast("error", message);
    }
  };

  const columns = [
    {
      title: "Holiday",
      dataIndex: "title",
      key: "title",
      width: "45%",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      title: "Delete",
      key: "delete",
      width: 130,
      render: (_: unknown, record: any) => (
        <Popconfirm
          title="Delete this holiday?"
          description="This will remove this global holiday for all businesses."
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: isDeletingSingleHoliday }}
          onConfirm={() => handleDeleteSingleGlobalHoliday(record)}
        >
          <Button danger loading={isDeletingSingleHoliday}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <SidebarLayout title="Holiday Calender">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 max-w-[920px]">
        <h3 className="text-lg font-semibold mb-2">Global Holiday Import</h3>
        <p className="text-sm text-[#6B7280] mb-5">
          Upload holiday calendar excel and apply holidays for all businesses
          globally.
        </p>

        {isSuperAdmin ? (
          <div className="flex items-center gap-3">
            <Button
              type="primary"
              className="middle-button"
              onClick={() => setIsImportModalOpen(true)}
            >
              Upload Holiday Calendar
            </Button>
            <Popconfirm
              title="Delete imported global holidays?"
              description={`This will delete only imported global holidays for ${selectedYear}. Business-added holidays will not be deleted.`}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: isDeletingGlobalHoliday }}
              onConfirm={handleDeleteGlobalHolidays}
            >
              <Button danger loading={isDeletingGlobalHoliday}>
                Delete Global Holiday
              </Button>
            </Popconfirm>
          </div>
        ) : (
          <p className="text-sm text-[#EF4444]">
            You do not have permission to import global holiday calendar.
          </p>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mt-6 max-w-[920px]">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Holiday List</h3>
          <Select
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
            options={Array.from({ length: 7 }, (_, index) => {
              const year = 2024 + index;
              return { value: year, label: String(year) };
            })}
            className="w-[140px]"
          />
        </div>

        {!importApiKey ? (
          <p className="text-sm text-[#EF4444]">
            Missing `REACT_APP_IMPORT_GLOBAL_API_KEY` for global holiday list.
          </p>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={holidays}
            loading={isLoading || isFetching}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description="No holidays found" /> }}
          />
        )}
      </div>

      <HolidayImportModal
        open={isImportModalOpen}
        handleClose={() => setIsImportModalOpen(false)}
      />
    </SidebarLayout>
  );
}
